import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ClickAwayListener } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Fuse from 'fuse.js';

import { SearchField } from 'lib';
import { USER_SEARCH_KEYS } from '../../constants';
import { styles } from './styles';

const useStyles = makeStyles(styles);

// Timeclock users may have no profile, so every part but the username is optional.
const toOption = ({ userId, username, employeeNum, fullName }) => ({
  value: userId,
  label: [username, employeeNum && `#${employeeNum}`, fullName].filter(Boolean).join(' / '),
});

const UserSearch = ({ users, value, onChange, className }) => {
  const classes = useStyles();
  const fuse = useMemo(
    () => new Fuse(users, { shouldSort: true, keys: USER_SEARCH_KEYS }),
    [users]
  );

  const lastLabel = useRef('');
  const selectedLabel = useMemo(() => {
    const selected = users.find((user) => user.userId === value);

    if (selected) lastLabel.current = toOption(selected).label;
    if (!value) lastLabel.current = '';
    return lastLabel.current;
  }, [users, value]);

  const [query, setQuery] = useState(selectedLabel);

  useEffect(() => setQuery(selectedLabel), [selectedLabel]);

  const matches = query && query !== selectedLabel ? fuse.search(query) : users;

  return (
    <ClickAwayListener onClickAway={() => setQuery(selectedLabel)}>
      <div className={classnames(classes.root, className)}>
        <SearchField
          value={query}
          options={matches.map(toOption)}
          placeholder="Search"
          onChange={(next) => {
            setQuery(next);
            if (!next) onChange('');
          }}
          onSelect={(option) => {
            setQuery(option.label);
            onChange(option.value);
          }}
        />
      </div>
    </ClickAwayListener>
  );
};

UserSearch.propTypes = {
  users: PropTypes.arrayOf(PropTypes.shape({ userId: PropTypes.string.isRequired })).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};

UserSearch.defaultProps = {
  value: '',
  className: undefined,
};

export default UserSearch;
